import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import getConfig from 'next/config';
import { getImageUrl } from '@culture/utils/helpers/files';

import { ImageFormatContext } from 'contexts/ImageFormatContext';

import EntityPropTypes from 'propTypes/entities';

import ImagesContainer from '../ImagesContainer';
import GridImage from '../GridImage';

import styles from './styles.module.scss';

const { publicRuntimeConfig } = getConfig();

const PromoBlock = ({ entity, inSlider, onFocus }) => {
	const { title, text, url, grid } = entity;
	const isLazySlider = publicRuntimeConfig.features.lazy && inSlider;
	const imageFormat = useContext(ImageFormatContext);

	const getImage = (file) =>
		getImageUrl(file, {
			config: publicRuntimeConfig,
			format: imageFormat,
			params: {
				crop: 'fill',
				gravity: 'center'
			}
		});

	const getColsInRow = (columns) =>
		columns.reduce((memoized, value) => {
			const newValue = value.cols ? getColsInRow(value.cols) : [value];

			return [...memoized, ...newValue];
		}, []);

	const getGridStyles = (block) => {
		const colsInRow = getColsInRow(block);
		const totalColsInRow = colsInRow.reduce(
			(memoized, value) => memoized + value.width,
			0
		);
		const firstColWidth = 100 / totalColsInRow;

		return block.reduce(
			(memoized, value) => {
				const params = {
					gridTemplateColumns: `${memoized.gridTemplateColumns} ${
						firstColWidth * value.width
					}fr`
				};

				return params;
			},
			{
				gridTemplateColumns: ''
			}
		);
	};

	const renderGrid = (cell) => {
		const getImageFile = (element) => element.file && getImage(element.file);

		if (cell.cols) {
			return (
				<ImagesContainer style={getGridStyles(cell.cols)}>
					{cell.cols.map((column, index) => {
						const image = getImageFile(column);

						return image ? (
							<GridImage
								key={image}
								image={image}
								isLazySlider={isLazySlider}
							/>
						) : (
							// eslint-disable-next-line react/no-array-index-key
							<React.Fragment key={`column-${index}`}>
								{renderGrid(column)}
							</React.Fragment>
						);
					})}
				</ImagesContainer>
			);
		}

		if (cell.rows) {
			return (
				<ImagesContainer style={{ gridTemplateRows: '1fr 1fr' }}>
					{cell.rows.map((row, index) => (
						// eslint-disable-next-line react/no-array-index-key
						<React.Fragment key={`row-${index}`}>
							{renderGrid(row)}
						</React.Fragment>
					))}
				</ImagesContainer>
			);
		}

		const image = getImageFile(cell);

		return image ? (
			<ImagesContainer>
				<GridImage image={image} isLazySlider={isLazySlider} />
			</ImagesContainer>
		) : null;
	};

	return (
		<a
			href={url}
			className={styles.GridPromoBlock}
			target="_blank"
			rel="noopener"
			onFocus={onFocus}
		>
			<div className={styles.GridPromoBlock__Content}>
				<div className={styles.GridPromoBlock__Title}>{title}</div>
				<div className={styles.GridPromoBlock__Description}>{text}</div>
			</div>
			<div className={styles.GridPromoBlock__WrapGrid}>
				<div className={styles.GridPromoBlock__InnerGrid}>
					{renderGrid(grid)}
				</div>
			</div>
		</a>
	);
};

PromoBlock.propTypes = {
	entity: EntityPropTypes.incutPromoBlock.isRequired,
	inSlider: PropTypes.bool,
	onFocus: PropTypes.func
};

PromoBlock.defaultProps = {
	inSlider: false
};

export default memo(PromoBlock);
